import { LoadingSpinner } from '../shared/components';
import { Formatters } from '../shared/lib/formatters';
import { closeTextOf, parseTs } from '../../../lib/guestRecovery.mjs';

// R83 #5 (Jesse: "Let's build out owner profiles a bit more! … Really need a
// full fledged owner profile system"): click an owner → their whole file.
// Photo, contact, properties + performance, documents (contracts / invoices /
// receipts / quotes needing approval / statements), a two-sided task list
// (what WE owe the owner vs what the OWNER owes us), a notes timeline, plus
// the per-property PDS Owner Notes. Built with the future owner login in
// mind — every block here is what a portal would later show read-only.
// Financial columns (revenue / owner net) follow the house role model:
// admin + director only; everyone office-side sees occupancy/ADR.
const esc = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));
const KIND_META: Record<string, { label: string; icon: string }> = {
  contract: { label: 'Contract', icon: '📄' },
  w9: { label: 'W-9', icon: '🪪' },            // R100 C4: replaces the Drive link
  form590: { label: 'Form 590', icon: '🏛️' },
  invoice: { label: 'Invoice', icon: '🧾' },
  receipt: { label: 'Receipt', icon: '🧾' },
  quote: { label: 'Quote', icon: '💬' },
  statement: { label: 'Statement', icon: '📊' },
  revenue_projection: { label: 'Revenue projection', icon: '📈' },   // R109 #h — the owner sees it on their portal (server OWNER_PORTAL_DOC_KINDS)
  other: { label: 'Other', icon: '📎' },
};

export class OwnerProfilePage {
  private container: HTMLElement;
  private ownerId = '';
  private data: any = null;            // /api/owners/:id/profile payload; {error} on failure
  private perf: any = null;            // /api/owners/:id/report payload (lazy, slow-ish)
  private perfState: 'idle' | 'loading' | 'error' | 'done' = 'idle';
  private pdsNotes: any[] = [];        // /api/ob/owner-notes rows for THIS owner's listings
  private declined: any[] = [];        // R114: Guest Recovery cases closed "Blocked by owner" on THIS owner's listings (office only)
  private docFilter = '';              // '' = all kinds
  private perfMonth = '';              // review #5: which month this.perf actually holds
  private newDocKind = 'contract';     // review #6: survives redraws (the select is rebuilt by draw())
  private chain: Promise<any> = Promise.resolve();   // serialized profile writes
  private busy = false;                // upload in flight — disable the pickers
  // R109 #h (CEO: "allow the file name to be edited"): one document row at a
  // time is in edit mode — name + type. State-backed so a background redraw
  // can't eat the typing.
  private renamingDoc: string | null = null;
  private renameDraft = '';
  private renameKind = '';
  private renameBusy = false;
  // R117: the owner's portal logins (several per owner — a spouse gets their own)
  private logins: any[] | null = null;
  private loginsErr = '';
  private loginMsg = '';
  private loginLink: string | null = null;
  private inviteOpen = false;

  constructor(container: HTMLElement) { this.container = container; }

  private who(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || 'office'); } catch { return 'office'; }
  }
  private isFin(): boolean {
    const r = String(sessionStorage.getItem('userRole') || '');
    return r === 'admin' || r === 'director';
  }
  private month(): string {
    const el = document.getElementById('global-month') as HTMLInputElement | null;
    return (el && /^\d{4}-\d{2}$/.test(el.value)) ? el.value : new Date().toISOString().slice(0, 7);
  }

  async render(): Promise<void> {
    const pre = sessionStorage.getItem('owner_profile_id');
    if (pre) { sessionStorage.removeItem('owner_profile_id'); this.ownerId = pre; this.data = null; this.perf = null; this.perfState = 'idle'; this.perfMonth = ''; this.pdsNotes = []; this.declined = []; this.docFilter = ''; this.renamingDoc = null; }
    this.container.innerHTML = `<div class="page-content"><div id="op-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    if (!this.ownerId) {
      this.body().innerHTML = `<div class="dash-head"><h1>Owner profile</h1><p>Open a profile from the Owners list (Owner Operations → Owners, or Admin → Owners).</p></div>
        <button class="btn btn-secondary" id="op-back">← Owners</button>`;
      this.bindBack();
      return;
    }
    await this.load();
  }

  private body(): HTMLElement { return document.getElementById('op-body') || this.container; }

  private async load(): Promise<void> {
    try {
      const r = await fetch(`/api/owners/${encodeURIComponent(this.ownerId)}/profile`);
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.data = j;
    } catch (e: any) {
      this.data = { error: e.message };
    }
    this.draw();
    if (this.data && !this.data.error) {
      this.loadPerf();
      this.loadPdsNotes();
      this.loadDeclined();
      void this.loadLogins();
    }
  }
  // R117: portal logins for this owner
  private async loadLogins(): Promise<void> {
    const id = this.ownerId;
    try {
      const r = await fetch(`/api/owners/${encodeURIComponent(id)}/logins`);
      const j = await r.json().catch(() => ({}));
      if (id !== this.ownerId) return;
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.logins = j.logins || []; this.loginsErr = '';
    } catch (e: any) { this.logins = null; this.loginsErr = e.message || 'could not load the logins'; }
    this.draw();
  }
  private loginsCard(o: any): string {
    const ls = this.logins;
    const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
          <span style="font-size:13px; font-weight:700;">🔑 Portal logins</span>
          <span style="font-size:11.5px; color:var(--text-muted);">${ls ? ls.length : '…'}</span>
          <button class="btn btn-secondary btn-sm" id="op-login-add" style="margin-left:auto; min-height:32px;">＋ Invite a login</button>
        </div>
        ${this.loginsErr ? `<div style="font-size:12px; color:var(--danger-text,#991b1b);">${esc(this.loginsErr)}</div>` : ''}
        ${ls && !ls.length ? '<div style="font-size:12.5px; color:var(--text-muted);">No portal login yet — invite the owner (and a spouse or partner as a second login) when you are ready to roll the portal out.</div>' : ''}
        ${(ls || []).map((l: any) => `<div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:7px 0; border-top:1px solid var(--border-soft); font-size:12.5px;">
          <span style="flex:1; min-width:160px;"><strong>${esc(l.full_name || l.email)}</strong>${l.full_name ? ` <span style="color:var(--text-muted);">${esc(l.email)}</span>` : ''}</span>
          ${l.invite_accepted === false ? '<span class="pill pill-amber">invited</span>' : l.last_sign_in ? `<span class="pill pill-green" title="last signed in">${esc(fmt(l.last_sign_in))}</span>` : ''}
          ${l.is_active === false ? '<span class="pill pill-gray">inactive</span>' : ''}
          <button class="btn btn-ghost btn-sm" data-op-login-link="${esc(l.id)}" title="A fresh sign-in link to send them">Sign-in link</button>
          <button class="btn btn-ghost btn-sm" data-op-login-del="${esc(l.id)}" data-email="${esc(l.email)}" title="Remove this login (the owner and their other logins stay)" style="color:var(--danger,#dc2626);">Remove</button>
        </div>`).join('')}
        ${this.inviteOpen ? `<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; align-items:flex-end;">
          <div style="flex:1 1 160px;"><label for="op-login-name" style="font-size:11px; color:var(--text-muted);">Name</label><input id="op-login-name" value="${esc((ls && ls.length) ? '' : (o.name || ''))}" style="width:100%; box-sizing:border-box; min-height:36px; padding:6px 10px; border:1px solid var(--border); border-radius:8px;"></div>
          <div style="flex:1 1 200px;"><label for="op-login-email" style="font-size:11px; color:var(--text-muted);">Email</label><input id="op-login-email" type="email" value="${esc((ls && ls.length) ? '' : (o.email || ''))}" style="width:100%; box-sizing:border-box; min-height:36px; padding:6px 10px; border:1px solid var(--border); border-radius:8px;"></div>
          <button class="btn btn-primary btn-sm" id="op-login-send" style="min-height:36px;">Create login</button>
        </div>` : ''}
        ${this.loginMsg ? `<div role="status" style="font-size:12px; margin-top:8px; color:var(--text-muted);">${esc(this.loginMsg)}</div>` : ''}
        ${this.loginLink ? `<div style="display:flex; gap:6px; margin-top:6px;"><input readonly value="${esc(this.loginLink)}" style="flex:1; min-width:0; font-size:11.5px; padding:6px 8px; border:1px solid var(--border); border-radius:7px;"><button class="btn btn-secondary btn-sm" id="op-login-copy">Copy</button></div>
          <div style="font-size:11.5px; color:var(--text-muted); margin-top:4px;">Send this link to them — it signs them in and asks them to pick a password. It works once.</div>` : ''}
      </div>`;
  }

  private loadPerf(): void {
    const m = this.month();
    // Review #5: refetch when the global month moved; review #4: a stale
    // response from a previous owner/month must never land on this page.
    if (this.perfState === 'loading' || (this.perfState === 'done' && this.perfMonth === m)) return;
    this.perfState = 'loading';
    const id = this.ownerId;
    fetch(`/api/owners/${encodeURIComponent(id)}/report?month=${m}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (this.ownerId !== id) return; this.perf = j; this.perfState = j ? 'done' : 'error'; this.perfMonth = m; this.draw(); })
      .catch(() => { if (this.ownerId !== id) return; this.perfState = 'error'; this.draw(); });
  }

  private loadPdsNotes(): void {
    const id = this.ownerId;
    fetch('/api/ob/owner-notes')
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (this.ownerId !== id) return;   // review #4 class: stale response for a previous owner
        if (!j || !Array.isArray(j.rows)) return;
        const mine = new Set((this.data.properties || []).map((p: any) => String(p.id)));
        this.pdsNotes = j.rows.filter((row: any) => mine.has(String(row.listing_id)));
        if (this.pdsNotes.length) this.draw();
      })
      .catch(() => { /* the card just stays absent */ });
  }

  // R114 #6: guest requests this owner declined (Guest Recovery cases closed
  // "Blocked by owner" — fans / portable AC…) for the owner conversation.
  // Office only (the GET is RECOVERY_ROLES; this page is OWNER_PROFILE_ROLES,
  // the same set) — never part of the owner portal. Same stale-owner guard as
  // loadPdsNotes; the card stays hidden when there are none.
  private isOffice(): boolean {
    const r = String(sessionStorage.getItem('userRole') || '');
    return ['admin', 'director', 'manager', 'remote_ops'].includes(r);
  }
  private loadDeclined(): void {
    if (!this.isOffice()) return;
    const id = this.ownerId;
    fetch(`/api/assetops/issues/guest-recovery?state=blocked_owner&owner_id=${encodeURIComponent(id)}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (this.ownerId !== id) return;   // stale response for a previous owner
        if (!j || !Array.isArray(j.tickets) || !this.data || this.data.error) return;
        // belt and braces: only this owner's homes (an older server ignores owner_id)
        const mine = new Set((this.data.properties || []).map((p: any) => String(p.id)));
        this.declined = j.tickets
          .filter((t: any) => t && t.recovery_status === 'smoothed' && t.recovery_outcome === 'blocked_owner' && (String(t.owner_id || '') === String(id) || mine.has(String(t.listing_id))))
          .sort((a: any, b: any) => parseTs(b.smoothed_at || b.last_message_at) - parseTs(a.smoothed_at || a.last_message_at));
        if (this.declined.length) this.draw();
      })
      .catch(() => { /* the card just stays absent */ });
  }
  private declinedCard(): string {
    if (!this.declined.length) return '';
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div class="card-title" style="margin-bottom:2px;">⛔ Guest requests this owner declined</div>
        <div style="font-size:11px; color:var(--text-muted); margin-bottom:8px;">Guest Recovery cases closed as "Blocked by owner" — open one for the full activity.</div>
        ${this.declined.slice(0, 12).map((t: any) => `
          <button type="button" data-op-gr="${esc(t.id)}" style="display:block; width:100%; text-align:left; background:none; border:none; border-bottom:1px dashed var(--border-soft); padding:8px 0; cursor:pointer; font:inherit; color:inherit; min-height:36px;">
            <div style="font-size:11px; color:var(--text-muted);">${esc(this.fmtD(t.smoothed_at || t.last_message_at))} · ${esc(t.listing_name || t.listing_id)}</div>
            <div style="font-size:12.5px; font-weight:600; margin-top:2px;">${esc(t.summary || 'Guest request')}</div>
            ${closeTextOf(t) ? `<div style="font-size:12px; color:var(--text-muted); margin-top:2px; white-space:pre-wrap; word-break:break-word;">${esc(closeTextOf(t))}</div>` : ''}
          </button>`).join('')}
        ${this.declined.length > 12 ? `<div style="font-size:11px; color:var(--text-muted); margin-top:6px;">+${this.declined.length - 12} more on Guest Recovery → Blocked by owner.</div>` : ''}
      </div>`;
  }

  // One profile write → serialized POST; on success the caller patches state.
  private post(path: string, payload: any): Promise<any | null> {
    // Review #7: capture the owner at QUEUE time — the chained closure runs
    // later, and this.ownerId may point at a different owner by then. A
    // response for a stale owner returns null so callers never patch state.
    const oid = this.ownerId;
    const run = this.chain.then(async () => {
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(oid)}/profile/${path}`, {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ...payload, by: this.who() }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (oid !== this.ownerId) return null;
        return j;
      } catch (e: any) { alert(`Didn't save — ${e.message}`); return null; }
    });
    this.chain = run.catch(() => { /* chain survives failures */ });
    return run;
  }

  private fmtD(s: any): string {
    if (!s) return '—';
    const d = new Date(String(s));
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  }
  private fmtSize(n: any): string {
    const v = Number(n);
    if (!(v > 0)) return '';
    return v > 1048576 ? `${(v / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(v / 1024))} KB`;
  }
  private money(n: any): string {
    if (n == null || n === '') return '—';   // R119 review #23: an unknown figure is never "$0"
    const v = Number(n);
    return Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : '—';
  }
  // R119 review #28: "Owner net" is the month's Net Income — the statement's. Said under
  // the figure (compact), never mixed in silently.
  // R121 (Jesse: "I don't want to be estimating the final payout"): until the statement is
  // out it is the net BEFORE expenses (no cleaning guess, no "est.") — the tag says so.
  // R121 fix-verify #17-remaining: the markers an Owner net carries are read in ONE place, so a layout
  // (the mixed month's per-row cell) can't drop one: before = some home has no statement yet, printed =
  // some statement was read from its printed Net Income only (R119 review #23)
  private netBits(ladders: any[]): { before: boolean; printed: boolean } {
    const ls = ladders.filter(Boolean);
    return { before: ls.some(l => l.net_basis && l.net_basis !== 'statement'), printed: ls.some(l => l.printed_only) };
  }
  private netTag(ladders: any[]): string {
    const { before, printed } = this.netBits(ladders);
    const bits = [before ? 'before expenses' : '', printed ? 'as printed' : ''].filter(Boolean);
    return bits.length ? `<div class="op-net-tag" data-op-net-tag style="font-size:10px; font-weight:400; color:var(--text-muted); white-space:nowrap;">${esc(bits.join(' · '))}</div>` : '';
  }

  private draw(): void {
    // Review #3: every async continuation funnels through draw() — if the
    // user navigated away, #op-body is gone and body() would fall back to the
    // SHARED page container, painting a zombie profile over the current page.
    if (!document.getElementById('op-body')) return;
    // Review #6: a background draw (slow perf fetch, note-post completion)
    // must not eat mid-typing input — capture drafts + focus, restore after.
    const draftSel = ['#op-newnote', '[data-op-newtask="us"]', '[data-op-newtask="owner"]'];
    const drafts = new Map<string, { v: string; pos: number; focused: boolean }>();
    for (const sel of draftSel) {
      const el = this.body().querySelector(sel) as HTMLInputElement | null;
      if (el && (el.value || document.activeElement === el)) drafts.set(sel, { v: el.value, pos: el.selectionStart ?? el.value.length, focused: document.activeElement === el });
    }
    // R109 #h: the rename box's text lives in renameDraft; keep its focus + caret too
    const ae = document.activeElement as HTMLInputElement | null;
    const renamePos = ae && ae.dataset && ae.dataset.opDocnameInput ? (ae.selectionStart ?? ae.value.length) : null;
    const body = this.body();
    const j = this.data;
    if (!j) return;
    if (j.error) {
      body.innerHTML = `<div class="dash-head"><h1>Owner profile</h1><p style="color:var(--danger-text,#991b1b);">Couldn't load this owner — ${esc(j.error)}</p></div>
        <button class="btn btn-secondary" id="op-back">← Owners</button>`;
      this.bindBack();
      return;
    }
    const o = j.owner || {};
    const prof = j.profile || {};
    const props: any[] = j.properties || [];
    body.innerHTML = `
      <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:12px;">
        <button class="btn btn-secondary btn-sm" id="op-back">← Owners</button>
        <button class="btn btn-secondary btn-sm" id="op-report" title="This owner's monthly report deck">report →</button>
        <button class="btn btn-secondary btn-sm" id="op-portal" title="Preview the owner portal as this owner">portal preview →</button>
        <button class="btn btn-secondary btn-sm" id="op-message" title="Write to this owner — opens Owner Operations → Inbox on their portal conversation (it lands in their portal under Messages)">💬 Message</button>
        <button class="btn btn-secondary btn-sm" id="op-npslink" title="Copy this owner's NPS survey link — anonymous option built in">📮 NPS link</button>
        <button class="btn btn-secondary btn-sm" id="op-formlink" title="Copy this owner's intake-form link — their answers land here and on each home's PDS Owner Form section">📝 Owner form link</button>
      </div>
      ${this.headerCard(o, prof, props)}
      <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(340px, 1fr)); gap:14px; align-items:start;">
        <div style="display:flex; flex-direction:column; gap:14px; min-width:0;">
          ${this.perfCard(props)}
          ${this.docsCard(prof)}
        </div>
        <div style="display:flex; flex-direction:column; gap:14px; min-width:0;">
          ${this.loginsCard(o)}
          ${this.formCard(j)}
          ${this.tasksCard(prof)}
          ${this.notesCard(prof)}
          ${this.pdsNotesCard()}
          ${this.declinedCard()}
        </div>
      </div>`;
    this.bind();
    for (const [sel, d] of drafts) {
      const el = body.querySelector(sel) as HTMLInputElement | null;
      if (el && !el.value) { el.value = d.v; if (d.focused) { el.focus(); el.setSelectionRange(d.pos, d.pos); } }
    }
    if (renamePos != null) {
      const el = body.querySelector<HTMLInputElement>('[data-op-docname-input]');
      if (el) { el.focus(); el.setSelectionRange(renamePos, renamePos); }
    }
  }

  private headerCard(o: any, prof: any, props: any[]): string {
    const initials = String(o.name || '?').split(/\s+/).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
    const openTasks = ((prof.tasks || []) as any[]).filter(t => !t.done).length;
    const pendingQuotes = ((prof.docs || []) as any[]).filter(d => d.kind === 'quote' && d.status === 'pending').length;
    return `
      <div class="card card-elev" style="margin-bottom:14px; padding:16px 18px; display:flex; gap:16px; align-items:center; flex-wrap:wrap;">
        <label title="Click to ${prof.photo_url ? 'replace' : 'add'} the owner photo" style="cursor:pointer; flex:none; position:relative;">
          ${prof.photo_url
            ? `<img src="${esc(prof.photo_url)}" alt="" style="width:72px; height:72px; border-radius:50%; object-fit:cover; border:2px solid var(--border);">`
            : `<span style="width:72px; height:72px; border-radius:50%; background:var(--accent-soft,#eaf2ff); color:var(--accent,#2563eb); display:inline-flex; align-items:center; justify-content:center; font-size:24px; font-weight:700;">${esc(initials)}</span>`}
          <span style="position:absolute; right:-2px; bottom:-2px; background:var(--card,#fff); border:1px solid var(--border); border-radius:50%; width:24px; height:24px; display:inline-flex; align-items:center; justify-content:center; font-size:12px;">📷</span>
          <input type="file" id="op-photo" accept="image/*" style="display:none;" ${this.busy ? 'disabled' : ''}>
        </label>
        <div style="flex:1; min-width:220px;">
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
            <span style="font-size:20px; font-weight:800; letter-spacing:-0.02em;">${esc(o.name)}</span>
            <span class="pill ${o.active !== false ? 'pill-green' : 'pill-gray'}">${o.active !== false ? 'Active' : 'Inactive'}</span>
            ${o.created_at ? `<span style="font-size:11.5px; color:var(--text-muted);">since ${esc(this.fmtD(o.created_at))}</span>` : ''}
          </div>
          <div style="font-size:12.5px; color:var(--text-muted); margin-top:3px; display:flex; gap:14px; flex-wrap:wrap;">
            ${o.email ? `<span>✉️ <a href="mailto:${esc(o.email)}">${esc(o.email)}</a></span>` : ''}
            ${o.phone ? `<span>📞 ${esc(o.phone)}</span>` : ''}
            ${o.contracts_url ? `<span><a href="${esc(o.contracts_url)}" target="_blank" rel="noopener">📁 Drive contracts (legacy)</a></span>` : ''}
          </div>
          ${(o.members || []).length ? `<div style="font-size:12px; color:var(--text-muted); margin-top:4px;">👥 Co-owner${o.members.length === 1 ? '' : 's'}: ${o.members.map((m: any) => `<strong>${esc([m.first_name, m.last_name].filter(Boolean).join(' '))}</strong>${m.role ? ` (${esc(m.role)})` : ''}${m.email ? ` · <a href="mailto:${esc(m.email)}">${esc(m.email)}</a>` : ''}${m.phone ? ` · ${esc(m.phone)}` : ''}`).join(' &nbsp;|&nbsp; ')}</div>` : ''}
          ${o.mailing_address && (o.mailing_address.line1 || o.mailing_address.city) ? `<div style="font-size:12px; color:var(--text-muted); margin-top:3px;">📬 ${esc([o.mailing_address.line1, o.mailing_address.line2, [o.mailing_address.city, o.mailing_address.state].filter(Boolean).join(', '), o.mailing_address.zip].filter(Boolean).join(' · '))}</div>` : ''}
          ${(() => { const has = (k: string) => ((prof.docs || []) as any[]).some(d => d.kind === k); const items = [['contract', 'Contract'], ['w9', 'W-9'], ['form590', 'Form 590']]; return `<div style="display:flex; gap:5px; flex-wrap:wrap; margin-top:6px;">${items.map(([k, l]) => `<span class="pill ${has(k) ? 'pill-green' : 'pill-amber'}" title="${has(k) ? 'on file' : 'not uploaded yet — use Documents → Upload'}">${has(k) ? '✓' : '○'} ${l}</span>`).join('')}<span class="pill ${prof.form && prof.form.at ? 'pill-green' : 'pill-gray'}" title="${prof.form && prof.form.at ? `owner form submitted ${esc(String(prof.form.at).slice(0, 10))}` : 'owner form not submitted yet — send the link'}">${prof.form && prof.form.at ? '✓ owner form' : '○ owner form'}</span></div>`; })()}
          ${o.notes ? `<div style="font-size:12px; color:var(--text-muted); margin-top:5px; font-style:italic;">${esc(o.notes)}</div>` : ''}
        </div>
        <div style="display:flex; gap:10px; flex:none; flex-wrap:wrap;">
          <div style="text-align:center; min-width:74px;"><div class="num" style="font-size:22px; font-weight:700;">${props.length}</div><div style="font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted);">Properties</div></div>
          <div style="text-align:center; min-width:74px;"><div class="num" style="font-size:22px; font-weight:700; ${openTasks ? 'color:var(--warning-text,#92400e);' : ''}">${openTasks}</div><div style="font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted);">Open tasks</div></div>
          <div style="text-align:center; min-width:74px;"><div class="num" style="font-size:22px; font-weight:700; ${pendingQuotes ? 'color:var(--warning-text,#92400e);' : ''}">${pendingQuotes}</div><div style="font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted);">Quotes pending</div></div>
        </div>
      </div>`;
  }

  private perfCard(props: any[]): string {
    const fin = this.isFin();
    const perfRows = new Map<string, any>();
    for (const p of ((this.perf && this.perf.properties) || [])) perfRows.set(String(p.listing_id), p);
    const totals = (this.perf && this.perf.totals) || null;
    // R121 review #17 (a mixed month — one home's statement out, another's not — totalled the column as
    // net before expenses over every home while the statement home's row showed its Net Income, after
    // expenses: the Total was that home's expenses more than its rows): ONE basis per column. In a
    // mixed month every row shows its net before expenses (Total − commission) — the Total adds them
    // up — and a statement home says its Net Income under it.
    const ladders = ((this.perf && this.perf.properties) || []).map((p: any) => p && p.ladder).filter(Boolean);
    const mixed = (totals && totals.net_basis === 'mixed') || (ladders.some((l: any) => l.net_basis === 'statement') && ladders.some((l: any) => l.net_basis !== 'statement'));
    const nbeOf = (l: any): number | null => { if (!l) return null; const v = l.net_before_expenses != null ? l.net_before_expenses : l.total_after_commission; return v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v); };
    const netCell = (m: any): string => {
      if (!mixed || !m.ladder) return `${this.money(m.owner_net)}${this.netTag([m.ladder])}`;
      const stmt = m.ladder.net_basis === 'statement';
      // fix-verify #17-remaining (R119 review #23 on the row, not just the footer): a statement read from its
      // printed Net Income only says "as printed" on its net income line, the figure that is printed (its net
      // before expenses is unknown: "—"); a home without a statement keeps "before expenses" alone
      const { printed } = this.netBits([m.ladder]);
      const printedAttr = printed ? ' data-op-printed title="This statement couldn\'t be read line by line, so its net before expenses is unknown. Its Net Income is as printed; see the Statement PDF."' : '';
      const tag = ['before expenses', printed && !stmt ? 'as printed' : ''].filter(Boolean).join(' · ');
      return `${this.money(nbeOf(m.ladder))}<div class="op-net-tag" data-op-net-tag${printed && !stmt ? printedAttr : ''} style="font-size:10px; font-weight:400; color:var(--text-muted); white-space:nowrap;">${esc(tag)}</div>${stmt ? `<div data-op-net-income${printedAttr} style="font-size:10px; font-weight:400; color:var(--text-muted); white-space:nowrap;">net income ${esc(this.money(m.ladder.net_income != null ? m.ladder.net_income : m.owner_net))} · ${printed ? 'as printed' : 'statement'}</div>` : ''}`;
    };
    const totalNet = (): string => {
      if (!totals) return '';
      const sni = mixed && totals.statement_net_income != null ? `<div data-op-net-income style="font-size:10px; font-weight:400; color:var(--text-muted); white-space:nowrap;">net income ${esc(this.money(totals.statement_net_income))} on the statements out</div>` : '';
      // the server's Σ net before expenses (null when a statement reads from its printed Net Income only), else the rows' sum
      const rowsSum = (): number | null => { let t = 0; for (const l of ladders) { const v = nbeOf(l); if (v == null) return null; t += v; } return Math.round(t * 100) / 100; };
      const v = !mixed ? totals.owner_net : totals.net_before_expenses !== undefined ? totals.net_before_expenses : rowsSum();
      return `${this.money(v)}${this.netTag(((this.perf && this.perf.properties) || []).map((p: any) => p.ladder))}${sni}`;
    };
    const rows = props.map(p => {
      const m = perfRows.get(String(p.id));
      return `
        <tr>
          <td style="font-weight:600;">${esc(p.name || p.short_name || p.id)}<div style="font-size:10.5px; color:var(--text-muted); font-weight:400;">${esc([p.city, p.state].filter(Boolean).join(', '))}${p.bedrooms != null ? ` · ${esc(p.bedrooms)}bd/${esc(p.bathrooms ?? '?')}ba` : ''}</div></td>
          <td class="num" style="text-align:right;">${m && m.occupancy != null ? `${Math.round(Number(m.occupancy))}%` : '—'}</td>
          <td class="num" style="text-align:right;">${m && m.adr != null ? this.money(m.adr) : '—'}</td>
          ${fin ? `<td class="num" style="text-align:right;">${m ? this.money(m.revenue) : '—'}</td>
          <td class="num" style="text-align:right; font-weight:600;">${m ? netCell(m) : '—'}</td>` : ''}
        </tr>`;
    }).join('');
    // R119 review #24/#28: a month whose maintenance & supplies couldn't be read says so.
    // R121 review #19: Owner net without a statement is net BEFORE expenses — it never takes the logged
    // items off, so the note no longer says they are missing from it (that was the payout framing R121 removed)
    const unread = fin && this.perf && this.perf.expenses_unavailable ? ' · ⚠ logged work & supplies couldn\'t be read just now (Owner net is before expenses)' : '';
    const perfNote = this.perfState === 'loading' ? 'crunching this month…'
      : this.perfState === 'error' ? '⚠ performance data didn\'t load — the properties list still stands'
      : `${this.month()} · occupancy is forward-looking 30d${unread}`;
    return `
      <div class="card card-elev" style="padding:0; overflow:hidden;">
        <div class="card-head" style="padding:11px 16px;"><span class="card-title">🏠 Properties & performance</span><span style="font-size:11px; color:var(--text-muted); margin-left:auto;">${esc(perfNote)}</span></div>
        ${props.length ? `
        <div class="u-table-wrap"><table class="data-table">
          <thead><tr><th>Property</th><th style="text-align:right;">Occ 30d</th><th style="text-align:right;">ADR</th>${fin ? '<th style="text-align:right;">Revenue</th><th style="text-align:right;" title="The month\'s net income from the statement; until it is out, the net before expenses (no estimate). While only some statements are out, every home shows its net before expenses, with a statement\'s net income under it.">Owner net</th>' : ''}</tr></thead>
          <tbody>${rows}</tbody>
          ${fin && totals ? `<tfoot><tr style="font-weight:700; border-top:2px solid var(--border);"><td>Total</td><td></td><td></td><td class="num" style="text-align:right;">${this.money(totals.revenue)}</td><td class="num" style="text-align:right;" data-op-net-total>${totalNet()}</td></tr></tfoot>` : ''}
        </table></div>` : '<div style="padding:16px; font-size:12.5px; color:var(--text-muted);">No properties assigned yet — assign them in Admin → Properties.</div>'}
      </div>`;
  }

  private docsCard(prof: any): string {
    const docs: any[] = [...(prof.docs || [])].reverse();   // newest first
    const kinds = Object.keys(KIND_META);
    const counts = new Map<string, number>();
    for (const d of docs) counts.set(d.kind, (counts.get(d.kind) || 0) + 1);
    const shown = this.docFilter ? docs.filter(d => d.kind === this.docFilter) : docs;
    const chip = (k: string, label: string, n: number | null) =>
      `<button data-op-dockind="${esc(k)}" class="chip" style="font-size:11px; padding:3px 10px; border:1.5px solid ${this.docFilter === k ? 'var(--text,#0f172a)' : 'var(--border)'}; border-radius:999px; background:${this.docFilter === k ? 'var(--text,#0f172a)' : 'var(--card,#fff)'}; color:${this.docFilter === k ? '#fff' : 'var(--text-muted)'}; cursor:pointer;">${esc(label)}${n != null ? ` ${n}` : ''}</button>`;
    const statusSel = (d: any) => d.kind !== 'quote' ? '' : `
      <select data-op-docstatus="${esc(d.id)}" title="Quote approval" style="font-size:10.5px; padding:2px 5px; border:1px solid var(--border); border-radius:6px; background:${d.status === 'approved' ? 'var(--success-soft,#ecfdf5)' : d.status === 'declined' ? 'var(--danger-soft,#fef2f2)' : 'var(--warning-soft,#fffbeb)'};">
        ${['pending', 'approved', 'declined'].map(s => `<option value="${s}" ${d.status === s ? 'selected' : ''}>${s}</option>`).join('')}
      </select>`;
    return `
      <div class="card card-elev" style="padding:0; overflow:hidden;">
        <div class="card-head" style="padding:11px 16px; flex-wrap:wrap; display:flex; gap:8px; align-items:center;">
          <span class="card-title">📁 Documents</span>
          <label class="btn btn-secondary btn-sm" style="cursor:pointer; margin-left:auto; ${this.busy ? 'opacity:.6; pointer-events:none;' : ''}">${this.busy ? 'Uploading…' : '⬆ Upload'}<input type="file" id="op-docfile" style="display:none;" ${this.busy ? 'disabled' : ''}></label>
          <select id="op-dockind-new" title="What is this file?" style="font-size:11.5px; padding:4px 7px; border:1px solid var(--border); border-radius:7px;">
            ${kinds.map(k => `<option value="${k}" ${this.newDocKind === k ? 'selected' : ''}>${KIND_META[k].icon} ${KIND_META[k].label}</option>`).join('')}
          </select>
        </div>
        <div style="display:flex; gap:5px; flex-wrap:wrap; padding:10px 16px 2px;">
          ${chip('', 'All', docs.length)}
          ${kinds.filter(k => counts.get(k)).map(k => chip(k, `${KIND_META[k].icon} ${KIND_META[k].label}s`, counts.get(k)!)).join('')}
        </div>
        <div style="padding:8px 16px 14px;">
          ${shown.length ? shown.map(d => this.renamingDoc === d.id ? `
            <div style="display:flex; gap:8px; align-items:center; padding:7px 0; border-bottom:1px dashed var(--border-soft,#f1f5f9); flex-wrap:wrap;">
              <span style="flex:none;">${KIND_META[this.renameKind] ? KIND_META[this.renameKind].icon : '📎'}</span>
              <input type="text" data-op-docname-input="${esc(d.id)}" value="${esc(this.renameDraft)}" maxlength="160" aria-label="Document name" placeholder="Document name" style="flex:1; min-width:160px; font-size:12.5px; padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-family:inherit;">
              <select data-op-dockind-edit title="What is this file?" style="flex:none; font-size:11.5px; padding:4px 6px; border:1px solid var(--border); border-radius:6px;">
                ${(kinds.includes(this.renameKind) ? kinds : [...kinds, this.renameKind]).map(k => `<option value="${esc(k)}" ${this.renameKind === k ? 'selected' : ''}>${KIND_META[k] ? `${KIND_META[k].icon} ${KIND_META[k].label}` : esc(k)}</option>`).join('')}
              </select>
              <button data-op-docname-save="${esc(d.id)}" class="btn btn-secondary btn-sm" style="flex:none;" ${this.renameBusy ? 'disabled' : ''}>${this.renameBusy ? 'Saving…' : 'Save'}</button>
              <button data-op-docname-cancel class="btn btn-secondary btn-sm" style="flex:none;">Cancel</button>
              <div style="flex:1 1 100%; font-size:10.5px; color:var(--text-muted);">The owner sees this name on their portal. The file itself doesn't change.${d.original_name && d.original_name !== d.name ? ` Uploaded as “${esc(d.original_name)}”.` : ''}</div>
            </div>` : `
            <div style="display:flex; gap:8px; align-items:center; padding:7px 0; border-bottom:1px dashed var(--border-soft,#f1f5f9); flex-wrap:wrap;">
              <span style="flex:none;">${KIND_META[d.kind] ? KIND_META[d.kind].icon : '📎'}</span>
              <a href="${esc(d.url)}" target="_blank" rel="noopener" style="font-size:12.5px; font-weight:600; flex:1; min-width:160px; word-break:break-word;" ${d.original_name && d.original_name !== d.name ? `title="Uploaded as “${esc(d.original_name)}”"` : ''}>${esc(d.name)}</a>
              ${statusSel(d)}
              <span style="font-size:10.5px; color:var(--text-muted); flex:none;">${esc(this.fmtD(d.at))}${d.by ? ` · ${esc(d.by)}` : ''}${d.size ? ` · ${esc(this.fmtSize(d.size))}` : ''}</span>
              <button data-op-docren="${esc(d.id)}" title="Rename or change the type — the owner sees the new name" style="flex:none; font-size:10.5px; padding:2px 6px; border:1px solid var(--border); border-radius:6px; background:var(--card,#fff); color:var(--text-muted); cursor:pointer;">✎</button>
              <button data-op-docdel="${esc(d.id)}" title="Remove from the file list" style="flex:none; font-size:10.5px; padding:2px 6px; border:1px solid #fecaca; border-radius:6px; background:var(--card,#fff); color:#b91c1c; cursor:pointer;">✕</button>
              ${d.note ? `<div style="flex:1 1 100%; font-size:11px; font-style:italic; color:var(--text-muted);">📝 ${esc(d.note)}</div>` : ''}
            </div>`).join('')
          : `<div style="font-size:12.5px; color:var(--text-muted); padding:6px 0;">No documents yet — upload the contract, invoices, quotes for approval, receipts, and owner statements here.</div>`}
        </div>
      </div>`;
  }

  private tasksCard(prof: any): string {
    const tasks: any[] = prof.tasks || [];
    const side = (who: 'us' | 'owner', title: string, hint: string) => {
      const mine = tasks.filter(t => t.who === who);
      const open = mine.filter(t => !t.done);
      const done = mine.filter(t => t.done);
      const row = (t: any) => `
        <div style="display:flex; gap:7px; align-items:flex-start; padding:4px 0; ${t.escalated && !t.done ? 'background:var(--danger-soft,#fef2f2); border-radius:6px; padding-left:4px;' : ''}">
          <input type="checkbox" data-op-task="${esc(t.id)}" ${t.done ? 'checked' : ''} style="margin-top:2px;">
          <button data-op-taskesc="${esc(t.id)}" title="${t.escalated ? 'Escalated — click to de-escalate' : 'Escalate — floats to the top of the owner projects board'}" style="flex:none; border:none; background:none; cursor:pointer; padding:0; font-size:12px; ${t.escalated ? '' : 'opacity:.3; filter:grayscale(1);'}">⚠️</button>
          <span style="flex:1; font-size:12.5px; ${t.done ? 'text-decoration:line-through; color:var(--text-faint,#94a3b8);' : ''}">${esc(t.text)}<span style="font-size:10px; color:var(--text-faint,#94a3b8);"> · ${esc(t.by || '')} ${esc(this.fmtD(t.at))}</span></span>
          <button data-op-taskdel="${esc(t.id)}" style="flex:none; font-size:10px; padding:1px 5px; border:1px solid var(--border); border-radius:5px; background:var(--card,#fff); color:var(--text-muted); cursor:pointer;">✕</button>
        </div>`;
      return `
        <div style="flex:1; min-width:230px;">
          <div style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:4px;">${title} ${open.length ? `<span class="num" style="color:var(--warning-text,#92400e);">· ${open.length} open</span>` : ''}</div>
          ${open.map(row).join('') || '<div style="font-size:11.5px; color:var(--text-faint,#94a3b8); padding:2px 0;">nothing open</div>'}
          ${done.length ? `<details style="margin-top:2px;"><summary style="font-size:10.5px; color:var(--text-faint,#94a3b8); cursor:pointer;">${done.length} done</summary>${done.map(row).join('')}</details>` : ''}
          <div style="display:flex; gap:5px; margin-top:6px;">
            <input type="text" data-op-newtask="${who}" placeholder="${hint}" style="flex:1; min-width:120px; font-size:12px; padding:5px 8px; border:1px solid var(--border); border-radius:7px;">
            <button data-op-addtask="${who}" class="btn btn-secondary btn-sm">Add</button>
          </div>
        </div>`;
    };
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div class="card-title" style="margin-bottom:8px;">✅ Tasks</div>
        <div style="display:flex; gap:18px; flex-wrap:wrap;">
          ${side('us', 'For us — owed to the owner', 'e.g. send Q3 statement…')}
          ${side('owner', 'For the owner — we\'re waiting on', 'e.g. sign the new contract…')}
        </div>
      </div>`;
  }

  private notesCard(prof: any): string {
    const notes: any[] = [...(prof.notes || [])].reverse();   // newest first
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div class="card-title" style="margin-bottom:8px;">🗒 Notes</div>
        <div style="display:flex; gap:6px; margin-bottom:10px;">
          <input type="text" id="op-newnote" placeholder="Add a note — calls, decisions, context…" style="flex:1; font-size:12.5px; padding:7px 10px; border:1px solid var(--border); border-radius:8px;">
          <button id="op-addnote" class="btn btn-secondary btn-sm">Add</button>
        </div>
        ${notes.length ? notes.map(n => `
          <div style="padding:7px 0; border-bottom:1px dashed var(--border-soft,#f1f5f9); display:flex; gap:8px; align-items:flex-start;">
            <div style="flex:1; font-size:12.5px; white-space:pre-wrap; word-break:break-word;">${esc(n.text)}<div style="font-size:10.5px; color:var(--text-faint,#94a3b8); margin-top:2px;">${esc(n.by || '')} · ${esc(this.fmtD(n.at))}</div></div>
            <button data-op-notedel="${esc(n.id)}" style="flex:none; font-size:10px; padding:1px 5px; border:1px solid var(--border); border-radius:5px; background:var(--card,#fff); color:var(--text-muted); cursor:pointer;">✕</button>
          </div>`).join('') : '<div style="font-size:12px; color:var(--text-muted);">No notes yet.</div>'}
      </div>`;
  }

  // R100 B11: the owner's intake answers, labelled by the shared question list.
  private formCard(j: any): string {
    const f = j.form;
    const qs: any[] = j.form_questions || [];
    if (!f || !f.answers) return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div class="card-title" style="margin-bottom:2px;">📝 Owner form</div>
        <div style="font-size:12px; color:var(--text-muted);">Not submitted yet. Send the owner their link (📝 Owner form link above) — answers land here and in each home's PDS "Owner Form" section.</div>
      </div>`;
    const rows = qs.filter(q => f.answers[q.key]).map(q => { const a = f.answers[q.key]; return `<div style="padding:5px 0; border-bottom:1px dashed var(--border-soft,#f1f5f9);"><div style="font-size:11px; color:var(--text-muted);">${esc(q.pds_field || q.label)}</div><div style="font-size:12.5px; white-space:pre-wrap;">${esc(a.value || '')}${a.detail ? ` <span style="color:var(--text-muted);">— ${esc(a.detail)}</span>` : ''}</div></div>`; }).join('');
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div style="display:flex; gap:8px; align-items:center; margin-bottom:2px;"><span class="card-title">📝 Owner form</span><span style="font-size:11px; color:var(--text-muted); margin-left:auto;">submitted ${esc(this.fmtD(f.at))}${f.submitted_by ? ` by ${esc(f.submitted_by)}` : ''}</span></div>
        <div style="font-size:11px; color:var(--text-muted); margin-bottom:6px;">${Object.keys(f.answers).length} of ${qs.length} answered · also written to each home's PDS "Owner Form" section</div>
        ${rows}
      </div>`;
  }

  private pdsNotesCard(): string {
    if (!this.pdsNotes.length) return '';
    return `
      <div class="card card-elev" style="padding:14px 16px;">
        <div class="card-title" style="margin-bottom:2px;">🏡 How this owner likes to work</div>
        <div style="font-size:11px; color:var(--text-muted); margin-bottom:8px;">From each property's PDS "Owner Notes" section — edit them there.</div>
        ${this.pdsNotes.map((row: any) => `
          <div style="margin-bottom:8px;">
            <div style="font-size:12px; font-weight:700;">${esc(row.name)}</div>
            ${(row.notes || []).map((n: any) => `<div style="font-size:12px; padding:2px 0 2px 10px;"><strong>${esc(n.field)}</strong>${n.value ? ` — ${esc(n.value)}` : ''}${n.note ? ` <em style="color:var(--text-muted);">📝 ${esc(n.note)}</em>` : ''}</div>`).join('')}
          </div>`).join('')}
      </div>`;
  }

  // ── events ──────────────────────────────────────────────────────────────

  // R37 house hook (review #6): App's 15-min auto-refresh re-renders the page
  // wholesale — veto it while an upload is in flight or the user is typing.
  suspendAutoRefresh(): boolean {
    if (this.busy) return true;
    const ae = document.activeElement as HTMLElement | null;
    return !!(ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT') && ae.closest('#op-body'));
  }

  private bindBack(): void {
    document.getElementById('op-back')?.addEventListener('click', () => {
      sessionStorage.setItem('hub_tab', 'owners');
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-ops' }));
    });
  }

  private bind(): void {
    const body = this.body();
    this.bindBack();
    // R117: portal logins
    body.querySelector('#op-login-add')?.addEventListener('click', () => { this.inviteOpen = !this.inviteOpen; this.loginMsg = ''; this.loginLink = null; this.draw(); });
    body.querySelector('#op-login-send')?.addEventListener('click', async () => {
      const email = (body.querySelector('#op-login-email') as HTMLInputElement)?.value.trim();
      const full_name = (body.querySelector('#op-login-name') as HTMLInputElement)?.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email || '')) { this.loginMsg = 'Enter a valid email.'; this.draw(); return; }
      this.loginMsg = 'Creating…'; this.draw();
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(this.ownerId)}/logins`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, full_name }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.inviteOpen = false;
        this.loginMsg = `✓ Login created for ${email}.${j.link_warning ? ` ${j.link_warning}` : ''}`;
        this.loginLink = j.link || null;
        await this.loadLogins();
      } catch (e: any) { this.loginMsg = `Not created — ${e.message}`; this.draw(); }
    });
    body.querySelectorAll<HTMLButtonElement>('[data-op-login-link]').forEach(b => b.addEventListener('click', async () => {
      this.loginMsg = 'Making a sign-in link…'; this.loginLink = null; this.draw();
      try {
        const r = await fetch(`/api/users/${encodeURIComponent(b.dataset.opLoginLink || '')}/resend-invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode: 'link' }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.loginMsg = `✓ Fresh sign-in link for ${j.email || 'them'}.${j.warning ? ` ${j.warning}` : ''}`; this.loginLink = j.link || null;
      } catch (e: any) { this.loginMsg = `No link — ${e.message}`; }
      this.draw();
    }));
    body.querySelectorAll<HTMLButtonElement>('[data-op-login-del]').forEach(b => b.addEventListener('click', async () => {
      if (!confirm(`Remove the portal login ${b.dataset.email || ''}? They can no longer sign in. The owner record and any other logins stay.`)) return;
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(this.ownerId)}/logins/${encodeURIComponent(b.dataset.opLoginDel || '')}`, { method: 'DELETE' });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.loginMsg = '✓ Login removed.'; this.loginLink = null;
        await this.loadLogins();
      } catch (e: any) { this.loginMsg = `Not removed — ${e.message}`; this.draw(); }
    }));
    body.querySelector('#op-login-copy')?.addEventListener('click', async () => { try { await navigator.clipboard.writeText(this.loginLink || ''); this.loginMsg = '✓ Link copied.'; this.draw(); } catch (_) { /* select + copy by hand */ } });
    body.querySelectorAll<HTMLElement>('[data-op-gr]').forEach(el => el.addEventListener('click', () => {
      try { sessionStorage.setItem('gr_focus', el.dataset.opGr || ''); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'guest-recovery' }));
    }));
    body.querySelector('#op-report')?.addEventListener('click', () => {
      sessionStorage.setItem('reports_scope', this.ownerId);
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'reports' }));
    });
    body.querySelector('#op-portal')?.addEventListener('click', () => {
      sessionStorage.setItem('portal_preview_owner', this.ownerId);
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-portal' }));
    });
    // R118 (Jesse: "Where would the owner go to send a message?" — and where does the
    // team start one): the Inbox, focused on this owner's portal thread (fresh when
    // they have none). Focus key FIRST, then the hub tab.
    body.querySelector('#op-message')?.addEventListener('click', () => {
      try { sessionStorage.setItem('oin_focus', this.ownerId); sessionStorage.setItem('hub_tab', 'inbox'); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'owner-ops' }));
    });
    // Photo upload
    body.querySelector('#op-photo')?.addEventListener('change', async (e) => {
      const input = e.target as HTMLInputElement;
      const f = input.files && input.files[0];
      if (!f) return;
      const owner = this.ownerId;   // review #8: a slow upload may finish on another owner's page
      this.busy = true; this.draw();
      try {
        const fd = new FormData();
        fd.append('photo', f);
        const r = await fetch(`/api/owners/${encodeURIComponent(owner)}/profile/photo`, { method: 'POST', body: fd });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (this.ownerId === owner && this.data && !this.data.error) this.data.profile.photo_url = j.photo_url;
      } catch (err: any) { alert(`Photo upload failed — ${err.message}`); }
      this.busy = false;
      if (this.ownerId === owner) this.draw();
    });
    // Document upload
    body.querySelector('#op-dockind-new')?.addEventListener('change', (e) => {
      this.newDocKind = (e.target as HTMLSelectElement).value || 'other';   // review #6: survives the busy redraw
    });
    body.querySelector('#op-docfile')?.addEventListener('change', async (e) => {
      const input = e.target as HTMLInputElement;
      const f = input.files && input.files[0];
      if (!f) return;
      const kind = this.newDocKind;
      const owner = this.ownerId;   // review #8: a 40MB contract can outlive this page
      this.busy = true; this.draw();
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('kind', kind);
        fd.append('by', this.who());
        const r = await fetch(`/api/owners/${encodeURIComponent(owner)}/profile/doc`, { method: 'POST', body: fd });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (this.ownerId === owner && this.data && !this.data.error) this.data.profile.docs = [...(this.data.profile.docs || []), j.doc];
      } catch (err: any) { alert(`Upload failed — ${err.message}`); }
      this.busy = false;
      if (this.ownerId === owner) this.draw();
    });
    body.querySelectorAll<HTMLElement>('[data-op-dockind]').forEach(b => b.addEventListener('click', () => {
      this.docFilter = b.dataset.opDockind || '';
      this.draw();
    }));
    body.querySelectorAll<HTMLSelectElement>('[data-op-docstatus]').forEach(sel => sel.addEventListener('change', async () => {
      const id = sel.dataset.opDocstatus!;
      const j = await this.post('doc-update', { doc_id: id, status: sel.value });
      if (j && j.doc) {
        const d = (this.data.profile.docs || []).find((x: any) => x.id === id);
        if (d) Object.assign(d, j.doc);
      }
      this.draw();
    }));
    body.querySelectorAll<HTMLElement>('[data-op-docdel]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.opDocdel!;
      const d = (this.data.profile.docs || []).find((x: any) => x.id === id);
      if (!window.confirm(`Remove "${d ? d.name : 'this document'}" from the owner's file?`)) return;
      const j = await this.post('doc-delete', { doc_id: id });
      if (j) this.data.profile.docs = (this.data.profile.docs || []).filter((x: any) => x.id !== id);
      this.draw();
    }));
    // R109 #h: rename / re-type a document (the owner portal shows the new name)
    const docOf = (id: string) => ((this.data && this.data.profile && this.data.profile.docs) || []).find((x: any) => x.id === id);
    body.querySelectorAll<HTMLElement>('[data-op-docren]').forEach(b => b.addEventListener('click', () => {
      const d = docOf(b.dataset.opDocren || '');
      if (!d) return;
      this.renamingDoc = d.id; this.renameDraft = String(d.name || ''); this.renameKind = String(d.kind || 'other');
      this.draw();
      const inp = this.body().querySelector<HTMLInputElement>('[data-op-docname-input]');
      if (inp) { inp.focus(); inp.select(); }
    }));
    const saveRename = async () => {
      const id = this.renamingDoc;
      if (!id || this.renameBusy) return;
      const d = docOf(id);
      if (!d) { this.renamingDoc = null; this.draw(); return; }
      const name = this.renameDraft.replace(/\s+/g, ' ').trim();
      if (!name) { alert('Give the document a name.'); return; }
      const payload: any = { doc_id: id };
      if (name !== d.name) payload.name = name;
      if (this.renameKind && this.renameKind !== d.kind) payload.kind = this.renameKind;
      if (payload.name === undefined && payload.kind === undefined) { this.renamingDoc = null; this.draw(); return; }
      const owner = this.ownerId;
      this.renameBusy = true; this.draw();
      const j = await this.post('doc-update', payload);
      this.renameBusy = false;
      if (this.ownerId !== owner) return;
      if (j && j.doc) { Object.assign(d, j.doc); this.renamingDoc = null; }   // a failed save keeps the box open with the typing
      this.draw();
    };
    const renInput = body.querySelector<HTMLInputElement>('[data-op-docname-input]');
    renInput?.addEventListener('input', () => { this.renameDraft = renInput.value; });
    renInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); void saveRename(); }
      else if (e.key === 'Escape') { e.preventDefault(); this.renamingDoc = null; this.draw(); }
    });
    body.querySelector<HTMLSelectElement>('[data-op-dockind-edit]')?.addEventListener('change', (e) => {
      this.renameKind = (e.target as HTMLSelectElement).value;
    });
    body.querySelector('[data-op-docname-save]')?.addEventListener('click', () => void saveRename());
    body.querySelector('[data-op-docname-cancel]')?.addEventListener('click', () => { this.renamingDoc = null; this.draw(); });
    // Tasks
    let addBusy = false;   // review #9: double-Enter during the round trip queued duplicate writes
    const addTask = async (who: string) => {
      if (addBusy) return;
      const input = body.querySelector(`[data-op-newtask="${who}"]`) as HTMLInputElement | null;
      const text = (input?.value || '').trim();
      if (!text) { input?.focus(); return; }
      addBusy = true;
      if (input) input.value = '';   // clear now so the restore-draft pass can't resurrect it
      try {
        const j = await this.post('task', { op: 'add', who, text });
        if (j && Array.isArray(j.tasks)) this.data.profile.tasks = j.tasks;
        else if (input && !input.value) input.value = text;   // failed — give the typing back
      } finally { addBusy = false; }
      this.draw();
    };
    body.querySelectorAll<HTMLElement>('[data-op-addtask]').forEach(b => b.addEventListener('click', () => void addTask(b.dataset.opAddtask!)));
    body.querySelectorAll<HTMLInputElement>('[data-op-newtask]').forEach(i => i.addEventListener('keydown', (e) => { if (e.key === 'Enter') void addTask(i.dataset.opNewtask!); }));
    body.querySelectorAll<HTMLElement>('[data-op-task]').forEach(cb => cb.addEventListener('change', async () => {
      const j = await this.post('task', { op: 'toggle', task_id: cb.dataset.opTask });
      if (j && Array.isArray(j.tasks)) this.data.profile.tasks = j.tasks;
      this.draw();
    }));
    body.querySelectorAll<HTMLElement>('[data-op-taskesc]').forEach(b => b.addEventListener('click', async () => {
      const j = await this.post('task', { op: 'escalate', task_id: b.dataset.opTaskesc });
      if (j && Array.isArray(j.tasks)) this.data.profile.tasks = j.tasks;
      this.draw();
    }));
    body.querySelector('#op-formlink')?.addEventListener('click', async () => {
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(this.ownerId)}/form-link`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ by: this.who() }) });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        try { await navigator.clipboard.writeText(j.url); alert(`Owner form link copied:\n${j.url}\n\nSend it to the owner — their answers land on this profile and on each home's PDS Owner Form section.`); }
        catch { window.prompt('Owner form link (copy it):', j.url); }
      } catch (e: any) { alert(`Couldn't get the link — ${e.message}`); }
    });
    body.querySelector('#op-npslink')?.addEventListener('click', async () => {
      try {
        const r = await fetch(`/api/owners/${encodeURIComponent(this.ownerId)}/nps-link`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        await navigator.clipboard.writeText(j.url).catch(() => { window.prompt('Copy the survey link:', j.url); });
        alert(`📮 Survey link copied — send it to the owner:\n${j.url}`);
      } catch (e: any) { alert(`Couldn't mint the link — ${e.message}`); }
    });
    body.querySelectorAll<HTMLElement>('[data-op-taskdel]').forEach(b => b.addEventListener('click', async () => {
      const j = await this.post('task', { op: 'delete', task_id: b.dataset.opTaskdel });
      if (j && Array.isArray(j.tasks)) this.data.profile.tasks = j.tasks;
      this.draw();
    }));
    // Notes
    const addNote = async () => {
      if (addBusy) return;
      const input = body.querySelector('#op-newnote') as HTMLInputElement | null;
      const text = (input?.value || '').trim();
      if (!text) { input?.focus(); return; }
      addBusy = true;
      if (input) input.value = '';
      try {
        const j = await this.post('note', { op: 'add', text });
        if (j && Array.isArray(j.notes)) this.data.profile.notes = j.notes;
        else if (input && !input.value) input.value = text;
      } finally { addBusy = false; }
      this.draw();
    };
    body.querySelector('#op-addnote')?.addEventListener('click', () => void addNote());
    (body.querySelector('#op-newnote') as HTMLInputElement | null)?.addEventListener('keydown', (e) => { if (e.key === 'Enter') void addNote(); });
    body.querySelectorAll<HTMLElement>('[data-op-notedel]').forEach(b => b.addEventListener('click', async () => {
      if (!window.confirm('Delete this note?')) return;
      const j = await this.post('note', { op: 'delete', note_id: b.dataset.opNotedel });
      if (j && Array.isArray(j.notes)) this.data.profile.notes = j.notes;
      this.draw();
    }));
  }
}
